import React, { useState, useEffect, useCallback } from 'react';
import authService from '../../services/authService';
import EmailChannelForm from './channels/EmailChannelForm';
import SlackChannelForm from './channels/SlackChannelForm';
import PagerDutyChannelForm from './channels/PagerDutyChannelForm';
import WebhookChannelForm from './channels/WebhookChannelForm';
import Toast from '../Toast';

const AlertChannels = ({ orgId, teamId }) => {
  const [channels, setChannels] = useState([]);
  const [authorizations, setAuthorizations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);
  const [testingChannel, setTestingChannel] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmToast, setConfirmToast] = useState({ show: false, message: '', onConfirm: null, onCancel: null });
  
  // Determine if we're in team mode
  const isTeamMode = !!teamId && !orgId;
  const entityId = isTeamMode ? teamId : orgId;

  // API helper
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = authService.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    };

    try {
      const response = await fetch(endpoint, {
        ...options,
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        const text = await response.text();
        throw new Error(`Unexpected response type: ${contentType}. Response: ${text.substring(0, 100)}`);
      }
    } catch (err) {
      console.error('API call error:', err);
      if (err instanceof TypeError && err.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw err;
    }
  }, []);

  // Load channels
  const loadChannels = useCallback(async () => {
    if (!entityId) return;

    setLoading(true);
    setError(null);

    try {
      const endpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}`
        : `/api/alertchannelsapi/organization/${orgId}`;
      const data = await apiCall(endpoint);
      setChannels(data.channels || []);
      setAuthorizations(data.authorizations || {});
    } catch (err) {
      setError(err.message);
      console.error('Error loading channels:', err);
    } finally {
      setLoading(false);
    }
  }, [entityId, isTeamMode, teamId, orgId, apiCall]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // Delete channel
  const handleDelete = useCallback((channelId) => {
    setConfirmToast({
      show: true,
      message: 'Are you sure you want to delete this channel? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmToast({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          const endpoint = isTeamMode
            ? `/api/alertchannelsapi/team/${teamId}/${channelId}`
            : `/api/alertchannelsapi/${channelId}`;
          await apiCall(endpoint, {
            method: 'DELETE'
          });
          await loadChannels();
          setError(null);
          setToast({ show: true, message: 'Channel deleted successfully', type: 'success' });
        } catch (err) {
          setToast({ show: true, message: err.message || 'Failed to delete channel', type: 'error' });
        }
      },
      onCancel: () => {
        setConfirmToast({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  }, [apiCall, loadChannels, isTeamMode, teamId]);

  // Test channel
  const handleTest = useCallback(async (channelId) => {
    setTestingChannel(channelId);
    setTestResult(null);
    setError(null);

    try {
      const endpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}/${channelId}/test`
        : `/api/alertchannelsapi/${channelId}/test`;
      const result = await apiCall(endpoint, {
        method: 'POST'
      });
      setTestResult(result);
      await loadChannels(); // Refresh to update LastUsedAt
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTestingChannel(null);
    }
  }, [apiCall, loadChannels, isTeamMode, teamId]);

  // Set primary
  const handleSetPrimary = useCallback(async (channelId) => {
    try {
      const endpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}/${channelId}/setprimary`
        : `/api/alertchannelsapi/${channelId}/setprimary`;
      await apiCall(endpoint, {
        method: 'POST'
      });
      await loadChannels();
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [apiCall, loadChannels, isTeamMode, teamId]);

  // Handle channel saved
  const handleChannelSaved = useCallback(() => {
    setShowAddModal(false);
    setEditingChannel(null);
    setSelectedChannelType(null);
    loadChannels();
  }, [loadChannels]);

  // Get channel type icon
  const getChannelIcon = (type) => {
    // Handle both string and number types
    const typeStr = typeof type === 'number' 
      ? ['Email', 'Slack', 'PagerDuty', 'Webhook'][type] 
      : type;
    switch (typeStr) {
      case 'Email': return '📧';
      case 'Slack': return '💬';
      case 'PagerDuty': return '📟';
      case 'Webhook': return '🔗';
      default: return '❓';
    }
  };

  // Check if channel type is authorized
  const isChannelTypeAuthorized = (channelType) => {
    if (channelType === 'Email') return true;
    return authorizations[channelType] === true;
  };

  // Get available channel types
  const getAvailableChannelTypes = () => {
    // Teams are limited to email channels only
    if (isTeamMode) {
      return [
        { value: 'Email', label: 'Email', icon: '📧', alwaysAvailable: true }
      ];
    }

    const types = [
      { value: 'Email', label: 'Email', icon: '📧', alwaysAvailable: true },
      { value: 'Slack', label: 'Slack', icon: '💬', requiresAuth: true },
      { value: 'PagerDuty', label: 'PagerDuty', icon: '📟', requiresAuth: true },
      { value: 'Webhook', label: 'Webhook', icon: '🔗', requiresAuth: true }
    ];

    return types.filter(type => 
      type.alwaysAvailable || isChannelTypeAuthorized(type.value)
    );
  };

  if (!entityId) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        Please select {isTeamMode ? 'a team' : 'an organization'} to view notification channels.
      </div>
    );
  }

  return (
    <div>
      {isTeamMode && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          backgroundColor: '#eff6ff',
          color: '#1e40af',
          borderRadius: '6px',
          border: '1px solid #bfdbfe',
          fontSize: '14px'
        }}>
          <strong>ℹ️ Note:</strong> Teams are limited to email notification channels. For Slack, PagerDuty, or Webhook integrations, configure alerts at the organization level.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>Notification Channels</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={selectedChannelType || ''}
            onChange={(e) => {
              const type = e.target.value;
              if (type) {
                setSelectedChannelType(type);
                setShowAddModal(true);
                setEditingChannel(null);
              }
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <option value="">Add Channel...</option>
            {getAvailableChannelTypes().map(type => (
              <option key={type.value} value={type.value}>
                {type.icon} {type.label}
              </option>
            ))}
          </select>
          <button
            onClick={loadChannels}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '6px',
          border: '1px solid #fecaca'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {testResult && (
        <Toast
          message={testResult.message || testResult.error || (testResult.success ? 'Test notification sent successfully' : 'Test notification failed')}
          type={testResult.success ? 'success' : 'error'}
          show={true}
          onClose={() => setTestResult(null)}
          duration={10000}
        />
      )}

      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
          Loading channels...
        </div>
      ) : channels.length === 0 ? (
        <div style={{
          padding: '48px 24px',
          textAlign: 'center',
          border: '2px dashed #e5e7eb',
          borderRadius: '8px',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📬</div>
          <h4 style={{ margin: '0 0 8px 0', color: '#111827' }}>No notification channels configured</h4>
          <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
            Create a channel to receive alert notifications
          </p>
          <select
            value={selectedChannelType || ''}
            onChange={(e) => {
              const type = e.target.value;
              if (type) {
                setSelectedChannelType(type);
                setShowAddModal(true);
              }
            }}
            style={{
              padding: '10px 16px',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            <option value="">Select Channel Type...</option>
            {getAvailableChannelTypes().map(type => (
              <option key={type.value} value={type.value}>
                {type.icon} {type.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {channels
            .filter(channel => {
              // In team mode, only show email channels
              if (isTeamMode) {
                const typeStr = typeof channel.channelType === 'number' 
                  ? ['Email', 'Slack', 'PagerDuty', 'Webhook'][channel.channelType] 
                  : channel.channelType;
                return typeStr === 'Email';
              }
              return true;
            })
            .map((channel) => (
            <div
              key={channel.id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                ...(channel.isPrimary && {
                  borderColor: '#3b82f6',
                  borderWidth: '2px',
                  backgroundColor: '#eff6ff'
                })
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '24px' }}>{getChannelIcon(channel.channelType)}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {channel.name}
                      {channel.isPrimary && (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: '#3b82f6',
                          color: '#ffffff',
                          fontWeight: 500
                        }}>
                          Primary
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                      Type: {typeof channel.channelType === 'number' 
                        ? ['Email', 'Slack', 'PagerDuty', 'Webhook'][channel.channelType] 
                        : channel.channelType} | 
                      Status: <span style={{ color: channel.isActive ? '#059669' : '#dc2626' }}>
                        {channel.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {channel.lastUsedAt && (
                        <> | Last Used: {new Date(channel.lastUsedAt).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    // Convert enum number to string if needed
                    const channelTypeStr = typeof channel.channelType === 'number' 
                      ? ['Email', 'Slack', 'PagerDuty', 'Webhook'][channel.channelType] 
                      : channel.channelType;
                    setEditingChannel(channel);
                    setSelectedChannelType(channelTypeStr);
                    setShowAddModal(true);
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    console.log('Test button clicked for channel:', channel.id);
                    handleTest(channel.id);
                  }}
                  disabled={testingChannel === channel.id || !channel.isActive}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    cursor: (testingChannel === channel.id || !channel.isActive) ? 'not-allowed' : 'pointer',
                    opacity: (testingChannel === channel.id || !channel.isActive) ? 0.6 : 1
                  }}
                  title={!channel.isActive ? 'Channel must be active to test' : ''}
                >
                  {testingChannel === channel.id ? 'Testing...' : 'Test'}
                </button>
                {!channel.isPrimary && (
                  <button
                    onClick={() => handleSetPrimary(channel.id)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      backgroundColor: '#3b82f6',
                      color: '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    Set Primary
                  </button>
                )}
                <button
                  onClick={() => handleDelete(channel.id)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid #dc2626',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    color: '#dc2626',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && selectedChannelType && (
        <ChannelModal
          channelType={selectedChannelType}
          channel={editingChannel}
          orgId={orgId}
          teamId={teamId}
          authorizations={authorizations}
          onClose={() => {
            setShowAddModal(false);
            setEditingChannel(null);
            setSelectedChannelType(null);
            setTestResult(null);
          }}
          onSaved={handleChannelSaved}
          apiCall={apiCall}
        />
      )}

      {/* Toast Components */}
      <Toast 
        message={toast.message} 
        type={toast.type} 
        show={toast.show} 
        onClose={() => setToast({ ...toast, show: false })} 
      />
      <Toast 
        message={confirmToast.message} 
        type="warning"
        show={confirmToast.show} 
        persist={true}
        actionLabel="Confirm"
        onAction={confirmToast.onConfirm}
        secondaryActionLabel="Cancel"
        onSecondaryAction={confirmToast.onCancel}
        onClose={() => setConfirmToast({ ...confirmToast, show: false })} 
      />
    </div>
  );
};

// Channel Modal Component
const ChannelModal = ({ channelType, channel, orgId, teamId, authorizations, onClose, onSaved, apiCall }) => {
  const renderForm = () => {
    const commonProps = {
      channel,
      orgId,
      teamId,
      onSave: onSaved,
      onCancel: onClose,
      apiCall
    };

    switch (channelType) {
      case 'Email':
        return <EmailChannelForm {...commonProps} />;
      case 'Slack':
        return <SlackChannelForm {...commonProps} />;
      case 'PagerDuty':
        return <PagerDutyChannelForm {...commonProps} />;
      case 'Webhook':
        return <WebhookChannelForm {...commonProps} />;
      default:
        return <div>Unknown channel type</div>;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }} onClick={(e) => e.stopPropagation()}>
        {renderForm()}
      </div>
    </div>
  );
};

export default AlertChannels;

